import { useMemo } from "react";

import type { SortDirection } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { entityListSortDirection } from "../entity-list/use-entity-list";

/** Row ordering for the card — the `entity-list` vocabulary, ordered on the
 *  server by the same `orderBy` that card pages over. */
export type AssignedSort = "created" | "updated" | "name";

export const ASSIGNED_SORT_LABEL: Record<AssignedSort, string> = {
  updated: "Updated",
  created: "Created",
  name: "Name",
};

/** The direction each sort faces by default — names A→Z, dates newest first
 *  — the same natural direction the project query encodes. */
export function assignedSortDirection(sort: AssignedSort): SortDirection {
  return entityListSortDirection(sort);
}

/** The card's own title. */
export const ASSIGNED_TITLE = "Assigned to me";

/** Every title the card generates — a stored title in this set is the card's
 *  own spelling and re-derives; any other stored title is the member's and
 *  stands. */
export const ASSIGNED_TITLE_FAMILY: ReadonlySet<string> = new Set([
  ASSIGNED_TITLE,
  "Projects assigned to me",
]);

/**
 * The Type filter as a project predicate: the server keys projects by type ID
 * and the filter holds type NAMES, so the names resolve to the workspace's
 * project type ids here. A name that is no project type at all leaves every
 * project OUT — the filter asked for a type projects do not have — hence the
 * empty `in`.
 */
export function useProjectTypeWhere(
  types: readonly string[],
): Record<string, unknown> | undefined {
  const { issueTypes } = useWorkspaceData();
  return useMemo(() => {
    if (types.length === 0) return undefined;
    const ids = issueTypes
      .filter((type) => types.includes(type.name))
      .map((type) => type.issueTypeId);
    return { issueTypeId: { in: ids } };
  }, [issueTypes, types]);
}
